/**
 * Pagination - Handles page navigation for AJAX-loaded content
 *
 * Supports two modes:
 * - "pages": Traditional pagination with page links (Laravel pagination)
 * - "loadMore": Appends new items (for infinite scroll / load more button)
 */

import type { PaginationConfig, PaginationMode, SearchResponse } from './types';

interface InternalPaginationConfig {
    containerSelector: string;
    resultsSelector: string;
    baseUrl: string;
    mode: PaginationMode;
    loadMoreSelector: string | null;
    loadingSelector: string | null;
    itemCountSelector: string | null;
    loadingText: string;
    loadMoreText: string;
    method: 'GET' | 'POST';
    updateHistory: boolean;
    csrfSelector: string | null;
    paginationSelector: string;
    onPageLoad: ((data: SearchResponse) => void) | null;
    onBeforeLoad: (() => void) | null;
    onAfterLoad: (() => void) | null;
    getParams: ((page: number) => URLSearchParams) | null;
}

export class Pagination {
    private readonly config: InternalPaginationConfig;
    private readonly container: HTMLElement;
    private readonly results: HTMLElement;
    private readonly loading: HTMLElement | null;
    private readonly loadMore: HTMLElement | null;
    private readonly itemCount: HTMLElement | null;

    private currentPage: number = 1;
    private isLoading: boolean = false;
    private abortController: AbortController | null = null;

    constructor(config: PaginationConfig) {
        this.config = {
            mode: 'pages',
            loadMoreSelector: null,
            loadingSelector: null,
            itemCountSelector: null,
            loadingText: 'Loading...',
            loadMoreText: 'Load more',
            method: 'GET',
            updateHistory: true,
            csrfSelector: null,
            paginationSelector: '.pagination a',
            onPageLoad: null,
            onBeforeLoad: null,
            onAfterLoad: null,
            getParams: null,
            ...config
        };

        const container = document.querySelector<HTMLElement>(this.config.containerSelector);
        const results = document.querySelector<HTMLElement>(this.config.resultsSelector);

        if (!container || !results) {
            throw new Error('Pagination: Container or results element not found');
        }

        this.container = container;
        this.results = results;

        this.loading = this.config.loadingSelector
            ? document.querySelector<HTMLElement>(this.config.loadingSelector)
            : null;

        this.loadMore = this.config.loadMoreSelector
            ? document.querySelector<HTMLElement>(this.config.loadMoreSelector)
            : null;

        this.itemCount = this.config.itemCountSelector
            ? document.querySelector<HTMLElement>(this.config.itemCountSelector)
            : null;

        this.bindEvents();
    }

    private bindEvents(): void {
        if (this.config.mode === 'loadMore') {
            this.loadMore?.addEventListener('click', (e: Event) => {
                e.preventDefault();
                void this.loadNextPage();
            });
        } else {
            // Event delegation for traditional pagination links
            this.container.addEventListener('click', (e: Event) => {
                const target = e.target as HTMLElement;
                const link = target.closest<HTMLAnchorElement>(this.config.paginationSelector);

                if (link) {
                    e.preventDefault();
                    const page = this.extractPageFromUrl(link.href);
                    void this.loadPage(link.href, page);
                }
            });
        }
    }

    private extractPageFromUrl(url: string): number {
        try {
            const urlObj = new URL(url, window.location.origin);
            const page = urlObj.searchParams.get('page');

            return page ? parseInt(page, 10) : 1;
        } catch {
            return 1;
        }
    }

    /**
     * Convert URL to use current origin (fixes mixed content issues)
     */
    private normalizeUrl(url: string): string {
        try {
            const urlObj = new URL(url, window.location.origin);

            // Use current origin to avoid mixed content issues
            return `${window.location.origin}${urlObj.pathname}${urlObj.search}`;
        } catch {
            return url;
        }
    }

    private showLoading(): void {
        // Show global loading bar
        window.Loading?.start();

        // Show dedicated loading element if exists
        this.loading?.classList.remove('d-none');

        if (this.loadMore) {
            this.loadMore.textContent = this.config.loadingText;
        }

        // Default visual feedback on results container
        this.results.style.opacity = '0.5';
        this.results.style.pointerEvents = 'none';
        this.container.style.cursor = 'wait';
    }

    private hideLoading(): void {
        // Force complete global loading bar (ensures it's hidden even if start() was called multiple times)
        window.Loading?.force();

        // Hide dedicated loading element
        this.loading?.classList.add('d-none');

        if (this.loadMore) {
            this.loadMore.textContent = this.config.loadMoreText;
        }

        // Reset visual feedback
        this.results.style.opacity = '';
        this.results.style.pointerEvents = '';
        this.container.style.cursor = '';
    }

    public async loadPage(url: string, page: number = 1): Promise<void> {
        // Cancel any pending request
        if (this.abortController) {
            this.abortController.abort();
        }

        const controller = new AbortController();
        this.abortController = controller;
        this.isLoading = true;
        this.currentPage = page;
        this.showLoading();
        this.config.onBeforeLoad?.();

        try {
            let response: Response;
            const params = this.config.getParams ? this.config.getParams(page) : new URLSearchParams();

            if (this.config.method === 'POST') {
                const formData = new FormData();

                // Add page to params if not already present
                if (!params.has('page') && page > 1) {
                    params.set('page', String(page));
                }

                params.forEach((value, key) => {
                    formData.append(key, value);
                });

                // Add CSRF token if configured
                if (this.config.csrfSelector) {
                    const csrfInput = document.querySelector<HTMLInputElement>(this.config.csrfSelector);

                    if (csrfInput) {
                        formData.append('_token', csrfInput.value);
                    }
                }

                response = await fetch(this.config.baseUrl, {
                    method: 'POST',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    body: formData,
                    signal: controller.signal
                });
            } else {
                response = await fetch(this.normalizeUrl(url), {
                    method: 'GET',
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    signal: controller.signal
                });
            }

            if (!response.ok) {
                console.error('Pagination: Failed to load page');

                return;
            }

            const data = await response.json() as SearchResponse;

            this.results.innerHTML = data.html;

            if (this.itemCount && data.total !== undefined) {
                this.itemCount.textContent = String(data.total);
            }

            // Update load more button visibility for loadMore mode
            if (this.config.mode === 'loadMore' && this.loadMore && data.total !== undefined) {
                const hasMore = this.results.children.length < data.total;
                this.loadMore.style.display = hasMore ? '' : 'none';
            }

            if (this.config.updateHistory) {
                if (this.config.method === 'POST') {
                    // For POST, build URL from params
                    const historyParams = new URLSearchParams(params);
                    const queryString = historyParams.toString();
                    const newUrl = queryString
                        ? `${window.location.pathname}?${queryString}`
                        : window.location.pathname;
                    history.pushState({}, '', newUrl);
                } else {
                    // Use relative path to avoid protocol issues
                    const urlObj = new URL(url, window.location.origin);
                    history.pushState({}, '', `${urlObj.pathname}${urlObj.search}`);
                }
            }

            if (this.config.onPageLoad) {
                this.config.onPageLoad(data);
            }
        } catch (error) {
            // Don't log abort errors - they're intentional cancellations
            if (error instanceof Error && error.name === 'AbortError') {
                return;
            }

            console.error('Pagination: Error loading page', error);
        } finally {
            // Only cleanup if this is still the active request
            if (this.abortController === controller) {
                this.abortController = null;
                this.isLoading = false;
                this.hideLoading();
                this.config.onAfterLoad?.();
            }
        }
    }

    public async loadNextPage(): Promise<void> {
        if (this.isLoading) {
            return;
        }

        // Cancel any pending request
        if (this.abortController) {
            this.abortController.abort();
        }

        const controller = new AbortController();
        this.abortController = controller;

        const nextPage = this.currentPage + 1;
        const params = this.config.getParams ? this.config.getParams(nextPage) : new URLSearchParams(window.location.search);
        params.set('page', String(nextPage));

        const url = this.normalizeUrl(`${this.config.baseUrl}?${params.toString()}`);

        this.isLoading = true;
        this.showLoading();
        this.config.onBeforeLoad?.();

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest'
                },
                signal: controller.signal
            });

            if (!response.ok) {
                console.error('Pagination: Failed to load next page');
                return;
            }

            const data = await response.json() as SearchResponse;

            this.results.insertAdjacentHTML('beforeend', data.html);
            this.currentPage++;

            if (this.loadMore && data.total !== undefined) {
                if (this.results.children.length >= data.total) {
                    this.loadMore.style.display = 'none';
                }
            }

            if (this.config.onPageLoad) {
                this.config.onPageLoad(data);
            }
        } catch (error) {
            // Don't log abort errors - they're intentional cancellations
            if (error instanceof Error && error.name === 'AbortError') {
                return;
            }

            console.error('Pagination: Error loading next page', error);
        } finally {
            // Only cleanup if this is still the active request
            if (this.abortController === controller) {
                this.abortController = null;
                this.isLoading = false;
                this.hideLoading();
                this.config.onAfterLoad?.();
            }
        }
    }

    public resetPage(): void {
        this.currentPage = 1;
    }

    public getCurrentPage(): number {
        return this.currentPage;
    }

    public getBaseUrl(): string {
        return this.config.baseUrl;
    }

    public getResults(): HTMLElement {
        return this.results;
    }

    public getIsLoading(): boolean {
        return this.isLoading;
    }
}

export default Pagination;
